<template>
  <div>
    <resource-form :endpoint="endpoint" :method="method" :schema="schema" :model="invoiceModel"
                   :width="900" :open_button="false"
                   @closed="$emit('closed')" ref="resourceForm">
      <template #title>{{ `app.invoice.invoice`|trans }}</template>
      <template #form="{ resource }">
        <v-flex xs4>
          <v-text-field v-model="resource.number"
                        required
                        :label="'app.number' | trans"
                        :rules="rules.number"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 v-if="latestFa !== null || latestFo !== null">
          <div class="caption grey--text">Ostatni FA: {{ latestFa || '-' }}</div>
          <div class="caption grey--text">Ostatni FO: {{ latestFo || '-' }}</div>
        </v-flex>
        <v-flex xs4>
          <v-text-field v-model="resource.customer_city"
                        required
                        :label="`app.city` | trans"
                        :rules="rules.customer_city"
          ></v-text-field>
        </v-flex>
        <v-flex xs4>
          <v-text-field v-model="resource.customer_name"
                        required
                        :label="resource.customer_type === 'company' ? `app.company`: `app.first_name_and_last_name` | trans"
                        :rules="rules.customer_name"
          ></v-text-field>
        </v-flex>
        <v-flex xs4>
          <v-text-field v-model="resource.customer_NIP"
                        :label="`app.nip` | trans"
                        :rules="rules.customer_NIP"
                        :prefix="nipCountryPrefix(resource.customer_country)"
          ></v-text-field>
        </v-flex>
        <v-flex xs4>
          <v-text-field v-model="resource.customer_postal_code"
                        required
                        :label="`app.postal_code` | trans"
                        :rules="rules.customer_postal_code"
          ></v-text-field>
        </v-flex>
        <v-flex xs4>
          <v-text-field v-model="resource.customer_street_address"
                        required
                        :label="`app.street_and_number` | trans"
                        :rules="rules.customer_street_address"
          ></v-text-field>
        </v-flex>
        <v-flex xs4>
          <v-autocomplete
              v-model="resource.customer_country"
              required
              :items="countries"
              :rules="rules.customer_country"
              item-text="name"
              item-value="code"
              :label="`app.country` | trans"
              @change="onCustomerCountryChange"
          >

          </v-autocomplete>
        </v-flex>
        <v-flex xs4>
          <v-select
              v-model="resource.customer_type"
              :items="customerTypes"
              :label="'app.type' | trans"
              @change="onCustomerTypeChange"
          ></v-select>
        </v-flex>
        <v-flex xs12>
          <v-textarea
            :label="'app.invoice.notes' | trans"
            :rules="rules.additionalInformation"
            v-model="resource.notes"
            full
            rows="4"
          ></v-textarea>
        </v-flex>
        <v-flex xs4 v-if="ksefInvoiceExists">
          <v-select
              v-model="ksefStatus"
              :items="ksefStatuses"
              item-text="text"
              item-value="value"
              :label="'app.ksef.ksef_status' | trans"
          ></v-select>
        </v-flex>
      </template>
      <template #action="{ resource }">
        <v-btn color="cancel" @click="refresh()">
          {{ 'app.refresh' | trans }}
        </v-btn>
      </template>
    </resource-form>
  </div>
</template>

<script>
import has from 'lodash/has';
import {trans} from '../../../app';
import {mapState, mapGetters} from 'vuex';
import checkVat, {EU_COUNTRIES} from '../../../services/vat/euVatValidator';
import {getVatCountryPrefix} from "../../../services/vat/vatCountryPrefix";

export default {

  props: {
    model: Object,
  },
  data: () => ({
    invoiceModel: null,
    ksefInvoiceExists: false,
    ksefStatus: null,
    originalKsefStatus: null,
    latestFa: null,
    latestFo: null,
    schema: {
      customer_city: null,
      customer_name: null,
      customer_NIP: null,
      customer_postal_code: null,
      customer_street_address: null,
      customer_country: null,
      customer_type: null,
      number: null,
    },
  }),
  watch: {
    model(val) {
      this.invoiceModel = val;
      if (val && val.order_id) {
        this.fetchKsefInvoice(val.order_id);
        this.fetchLatestInvoiceNumbers();
      } else {
        this.ksefInvoiceExists = false;
        this.ksefStatus = null;
        this.originalKsefStatus = null;
        this.latestFa = null;
        this.latestFo = null;
      }
    },
  },

  mounted() {
    this.$root.$on('stored', this.onInvoiceStored);
  },
  beforeDestroy() {
    this.$root.$off('stored', this.onInvoiceStored);
  },

  computed: {
    ...mapGetters(['countries', 'isKsefEnabled']),
    ...mapState(['dictionaries']),
    method() {
      return 'PATCH';
    },
    endpoint() {
      if (has(this.model, 'order_id')) {
        return `/administrator/orders/${this.model.order_id}/invoice`;
      }
      return '';
    },
    rules() {
      return {
        number: [
          v => !!v || trans('app.settings.validation.orders_and_invoices.number.required'),
        ],
        customer_name: [
          v => !!v || trans('app.settings.validation.orders_and_invoices.name.required'),
        ],
        customer_street_address: [
          v => !!v || trans('app.settings.validation.orders_and_invoices.street_address.required'),
        ],
        customer_postal_code: [
          v => !!v || trans('app.settings.validation.orders_and_invoices.postal_code.required'),
        ],
        customer_city: [
          v => !!v || trans('app.settings.validation.orders_and_invoices.city.required'),
        ],
        customer_NIP: [
          v => this.customerNIPValidation(v),
        ],
        customer_country: [
          v => !!v || trans('app.settings.validation.orders_and_invoices.country.required'),
        ],
        notes: [
          v => v && v.length <= 255 || trans('validation.max.string', {
            attribute: trans('app.invoice.notes'),
            max: 255,
          }),
        ],
      }
    },
    customerTypes() {
      return [
        {
          text: trans('app.private_person'),
          value: 'private_person',
        },
        {
          text: trans('app.company'),
          value: 'company'
        }
      ];
    },
    ksefStatuses() {
      return [
        { text: trans('app.ksef.ksef_statuses.to_send'), value: 'to_send' },
        { text: trans('app.ksef.ksef_statuses.sending'), value: 'sending' },
        { text: trans('app.ksef.ksef_statuses.sent'), value: 'sent' },
        { text: trans('app.ksef.ksef_statuses.processing_by_ksef'), value: 'processing_by_ksef' },
        { text: trans('app.ksef.ksef_statuses.accepted'), value: 'accepted' },
        { text: trans('app.ksef.ksef_statuses.rejected'), value: 'rejected' },
        { text: trans('app.ksef.ksef_statuses.failed'), value: 'failed' },
      ];
    },
  },
  methods: {
    customerNIPValidation(value) {
      if (!this.$refs.resourceForm) {
        return true;
      }

      const customerType = this.$refs.resourceForm.resource.customer_type;
      const customerCountry = this.$refs.resourceForm.resource.customer_country;

      if (customerType === 'private_person') {
        return true;
      }

      if (EU_COUNTRIES.includes(customerCountry)) {
        if (!value || !checkVat(customerCountry, value)) {
          const country = this.countries.find(c => c.code === customerCountry)?.name || customerCountry
          return trans('validation.vat_invalid', { country: country });
        }
        return true;
      }

      if (!value) {
        return trans('app.settings.validation.orders_and_invoices.nip.required');
      }

      return true;
    },
    nipCountryPrefix(country) {
      return getVatCountryPrefix(country);
    },
    fetchLatestInvoiceNumbers() {
      axios.get('/administrator/invoices/latest-numbers')
        .then((response) => {
          this.latestFa = response.data.latest_fa;
          this.latestFo = response.data.latest_fo;
        })
        .catch((error) => {
          this.sentryCaptureException(error);
        });
    },
    refresh() {
      let resource = this.$refs.resourceForm.resource
      axios.get(`/administrator/orders/${resource.order_id}/refresh_invoice`).then((response) => {
        const data = response.data;
        this.$refs.resourceForm.setValue('customer_city', data.city);
        this.$refs.resourceForm.setValue('customer_name', data.name);
        this.$refs.resourceForm.setValue('customer_NIP', data.nip);
        this.$refs.resourceForm.setValue('customer_country', data.country);
        this.$refs.resourceForm.setValue('customer_postal_code', data.postal_code);
        this.$refs.resourceForm.setValue('customer_street_address', data.street_address);
        this.$refs.resourceForm.setValue('customer_type', data.type);
        this.$refs.resourceForm.resetValidation();
      }).catch((error) => {
        this.sentryCaptureException(error);
      });
    },
    onCustomerTypeChange(customerType) {
      if (this.isKsefEnabled && customerType === 'private_person') {
        this.$refs.resourceForm.resetValidation();
      }
    },
    onCustomerCountryChange() {
      this.$nextTick(() => {
        if (this.$refs.resourceForm && this.$refs.resourceForm.$refs.form) {
          this.$refs.resourceForm.$refs.form.validate();
        }
      });
    },
    fetchKsefInvoice(orderUuid) {
      axios.get(`/administrator/orders/${orderUuid}/ksef-invoice`)
        .then((response) => {
          this.ksefInvoiceExists = true;
          this.ksefStatus = response.data.data.status;
          this.originalKsefStatus = response.data.data.status;
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            this.ksefInvoiceExists = false;
          } else {
            this.sentryCaptureException(error);
          }
        });
    },
    onInvoiceStored() {
      if (!this.ksefInvoiceExists || this.ksefStatus === this.originalKsefStatus) {
        return;
      }

      const orderUuid = this.invoiceModel.order_id;

      this.$root.$emit('ksef-save-started');

      axios.patch(`/administrator/orders/${orderUuid}/ksef-invoice`, {
        status: this.ksefStatus,
      }).then(() => {
        this.originalKsefStatus = this.ksefStatus;
      }).catch((error) => {
        this.sentryCaptureException(error);
      }).finally(() => {
        this.$root.$emit('ksef-save-finished');
      });
    },
  }
};
</script>
